import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShareIcon,
  UserGroupIcon,
  EnvelopeIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input, EmptyState } from '../components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

interface SharedFarm {
  shareId: string;
  farmId: string;
  farmName: string;
  areaHectares: number;
  cropType: string | null;
  ownerName: string;
  ownerEmail: string;
  permission: string;
  sharedAt: string;
}

interface FarmShareDetail {
  id: string;
  farmId: string;
  sharedWithId: string | null;
  sharedWithName: string | null;
  sharedWithEmail: string | null;
  invitedEmail: string;
  permission: string;
  status: string;
  createdAt: string;
}

interface Farm {
  id: string;
  name: string;
}

const permissionColors: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  editor: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-700',
};

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  revoked: 'bg-gray-100 text-gray-500',
};

export default function FarmSharing() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'shared' | 'my-shares' | 'share'>('shared');
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'editor' | 'viewer'>('viewer');
  const [lastInviteLink, setLastInviteLink] = useState('');

  // Fetch farms shared with me
  const { data: sharedFarms = [], isLoading: loadingShared } = useQuery({
    queryKey: ['shared-with-me'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/sharing/shared-with-me`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch shared farms');
      return res.json() as Promise<SharedFarm[]>;
    },
  });

  // Fetch my farms for the share form and for viewing shares
  const { data: myFarms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/farms`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch farms');
      return res.json() as Promise<Farm[]>;
    },
  });

  // Fetch shares I sent for a selected farm
  const [viewSharesFarmId, setViewSharesFarmId] = useState<string | null>(null);
  const { data: myShares = [], isLoading: loadingMyShares } = useQuery({
    queryKey: ['farm-shares', viewSharesFarmId],
    queryFn: async () => {
      if (!viewSharesFarmId) return [];
      const res = await fetch(`${API_URL}/sharing/farm/${viewSharesFarmId}/shares`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch shares');
      return res.json() as Promise<FarmShareDetail[]>;
    },
    enabled: !!viewSharesFarmId,
  });

  // Share mutation
  const shareMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/sharing`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ farmId: selectedFarmId, email: shareEmail, permission: sharePermission }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to share');
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast.success('Farm shared successfully!');
      setLastInviteLink(data.inviteLink || '');
      setShareEmail('');
      queryClient.invalidateQueries({ queryKey: ['farm-shares'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // Revoke mutation
  const revokeMutation = useMutation({
    mutationFn: async (shareId: string) => {
      const res = await fetch(`${API_URL}/sharing/${shareId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to revoke');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Share revoked');
      queryClient.invalidateQueries({ queryKey: ['farm-shares'] });
    },
    onError: () => toast.error('Failed to revoke share'),
  });

  const copyInviteLink = () => {
    navigator.clipboard.writeText(lastInviteLink);
    toast.success('Invite link copied!');
  };

  const tabs = [
    { key: 'shared', label: 'Shared With Me', icon: UserGroupIcon },
    { key: 'my-shares', label: 'My Shares', icon: ShareIcon },
    { key: 'share', label: 'Share a Farm', icon: EnvelopeIcon },
  ] as const;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Farm Sharing</h1>
        <p className="text-gray-500 mt-1">Share your farms with other users and manage access</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-8">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? 'bg-white text-kenagri-700 shadow-soft'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Shared With Me */}
      {activeTab === 'shared' && (
        <div>
          {loadingShared ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-48 bg-gray-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : sharedFarms.length === 0 ? (
            <EmptyState
              icon={UserGroupIcon}
              title="No shared farms"
              description="When someone shares a farm with you, it will appear here."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedFarms.map((farm) => (
                <Card key={farm.shareId} variant="gradient">
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 text-lg">{farm.farmName}</h3>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${permissionColors[farm.permission]}`}>
                        {farm.permission}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-500">
                      <p>Area: {farm.areaHectares?.toFixed(1)} ha</p>
                      {farm.cropType && <p>Crop: {farm.cropType}</p>}
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-gray-400">Shared by</p>
                        <p className="font-medium text-gray-700">{farm.ownerName}</p>
                        <p className="text-xs text-gray-400">{farm.ownerEmail}</p>
                      </div>
                      <p className="text-xs text-gray-400">
                        Since {new Date(farm.sharedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Shares */}
      {activeTab === 'my-shares' && (
        <div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Select a farm to view shares</label>
            <select
              value={viewSharesFarmId || ''}
              onChange={(e) => setViewSharesFarmId(e.target.value || null)}
              className="w-full max-w-md px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            >
              <option value="">-- Choose a farm --</option>
              {myFarms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          {viewSharesFarmId && (
            loadingMyShares ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />
                ))}
              </div>
            ) : myShares.length === 0 ? (
              <EmptyState
                icon={ShareIcon}
                title="No shares"
                description="This farm hasn't been shared with anyone yet."
              />
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">User / Email</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Permission</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myShares.map((share) => (
                      <tr key={share.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">
                            {share.sharedWithName || share.invitedEmail}
                          </p>
                          {share.sharedWithEmail && share.sharedWithEmail !== share.invitedEmail && (
                            <p className="text-xs text-gray-400">{share.sharedWithEmail}</p>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${permissionColors[share.permission]}`}>
                            {share.permission}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[share.status]}`}>
                            {share.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-500">
                          {new Date(share.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => revokeMutation.mutate(share.id)}
                            className="text-red-500 hover:text-red-700 transition-colors p-1"
                            title="Revoke access"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* Share a Farm */}
      {activeTab === 'share' && (
        <Card variant="gradient">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green">
                <EnvelopeIcon className="h-5 w-5 text-white" />
              </div>
              <CardTitle>Share a Farm</CardTitle>
            </div>
          </CardHeader>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Farm</label>
              <select
                value={selectedFarmId}
                onChange={(e) => setSelectedFarmId(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
              >
                <option value="">-- Select a farm --</option>
                {myFarms.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <Input
              label="Email Address"
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="collaborator@example.com"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Permission Level</label>
              <div className="flex gap-3">
                {(['viewer', 'editor'] as const).map((perm) => (
                  <button
                    key={perm}
                    onClick={() => setSharePermission(perm)}
                    className={`flex-1 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      sharePermission === perm
                        ? 'border-kenagri-500 bg-kenagri-50 text-kenagri-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {sharePermission === perm && <CheckCircleIcon className="h-4 w-4" />}
                      <span className="capitalize">{perm}</span>
                    </div>
                    <p className="text-xs mt-1 opacity-70">
                      {perm === 'viewer' ? 'Can view farm data' : 'Can view and edit farm data'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <Button
              variant="primary"
              size="lg"
              onClick={() => shareMutation.mutate()}
              disabled={!selectedFarmId || !shareEmail || shareMutation.isPending}
              leftIcon={shareMutation.isPending ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <ShareIcon className="h-5 w-5" />}
            >
              {shareMutation.isPending ? 'Sharing...' : 'Share Farm'}
            </Button>

            {lastInviteLink && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  <p className="font-medium text-green-800">Invitation sent!</p>
                </div>
                <p className="text-sm text-green-700 mb-3">Share this link with the invited user:</p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={lastInviteLink}
                    className="flex-1 px-3 py-2 bg-white border border-green-200 rounded-lg text-sm text-gray-700 font-mono"
                  />
                  <button
                    onClick={copyInviteLink}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
